import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Upload,
  X,
  CheckCircle2,
  XCircle,
  Loader,
  Clock,
  FileText,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_ICONS = {
  queued: <Clock className="w-4 h-4 text-gray-400" />,
  processing: <Loader className="w-4 h-4 text-ugmonk-yellow animate-spin" />,
  complete: <CheckCircle2 className="w-4 h-4 text-green-500" />,
  failed: <XCircle className="w-4 h-4 text-red-500" />,
};

const ACCEPTED_TYPES = '.pdf,.txt,.doc,.docx,.png,.jpg,.jpeg';

export default function BulkUploadModal({ open, onClose, sessionId, onComplete }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [fileStatuses, setFileStatuses] = useState({});
  const [progress, setProgress] = useState({ completed: 0, failed: 0, total: 0, eta_seconds: 0 });
  const [done, setDone] = useState(false);
  const [doneInfo, setDoneInfo] = useState(null);
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 100) {
      alert('Maximum 100 files allowed per upload.');
      return;
    }
    setSelectedFiles(files);
    setFileStatuses({});
    setProgress({ completed: 0, failed: 0, total: 0, eta_seconds: 0 });
    setDone(false);
    setDoneInfo(null);
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const startUpload = useCallback(async () => {
    if (!selectedFiles.length || !sessionId) return;

    setUploading(true);
    setDone(false);
    setDoneInfo(null);

    // Mark all files as queued
    const initial = {};
    selectedFiles.forEach((f) => {
      initial[f.name] = { status: 'queued', stage: '' };
    });
    setFileStatuses(initial);

    const formData = new FormData();
    selectedFiles.forEach((f) => formData.append('files', f));

    try {
      const response = await fetch(
        `${API}/chat/sessions/${sessionId}/files/bulk`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData,
        }
      );

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));

            if (data.event === 'init') {
              setProgress((p) => ({ ...p, total: data.total_files }));
            } else if (data.event === 'file_status') {
              setFileStatuses((prev) => ({
                ...prev,
                [data.filename]: {
                  status: data.status,
                  stage: data.stage || '',
                  error: data.error,
                  file_id: data.file_id,
                },
              }));
            } else if (data.event === 'progress') {
              setProgress({
                completed: data.completed,
                failed: data.failed,
                total: data.total,
                eta_seconds: data.eta_seconds,
              });
            } else if (data.event === 'done') {
              setDone(true);
              setDoneInfo(data);
            }
          } catch {
            // skip unparseable lines
          }
        }
      }
    } catch (err) {
      console.error('Bulk upload error:', err);
    } finally {
      setUploading(false);
    }
  }, [selectedFiles, sessionId]);

  const handleClose = () => {
    if (done && doneInfo && doneInfo.successful > 0) {
      onComplete?.();
    }
    setSelectedFiles([]);
    setFileStatuses({});
    setProgress({ completed: 0, failed: 0, total: 0, eta_seconds: 0 });
    setDone(false);
    setDoneInfo(null);
    onClose();
  };

  const pct = progress.total > 0 ? Math.round(((progress.completed + progress.failed) / progress.total) * 100) : 0;

  const formatEta = (seconds) => {
    if (!seconds || seconds <= 0) return '';
    if (seconds < 60) return `${Math.ceil(seconds)}s remaining`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.ceil(seconds % 60);
    return `${mins}m ${secs}s remaining`;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-ugmonk-dark" />
            Bulk File Upload
          </DialogTitle>
        </DialogHeader>

        {!uploading && !done && (
          <>
            <div
              className="border-2 border-dashed border-ugmonk-border rounded-lg p-8 text-center cursor-pointer hover:border-ugmonk-yellow hover:bg-[#fffdf0]/50 transition-all"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-10 h-10 mx-auto mb-3 text-gray-400" />
              <p className="text-sm font-medium text-gray-700">
                Click to select files (up to 100)
              </p>
              <p className="text-xs text-gray-500 mt-1">
                PDF, TXT, DOC, DOCX, PNG, JPG — 10MB max each
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED_TYPES}
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {selectedFiles.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
                </p>
                <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-2">
                  {selectedFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm py-1 px-2 rounded hover:bg-gray-50">
                      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="flex-1 truncate">{f.name}</span>
                      <span className="text-xs text-gray-400">{(f.size / 1024).toFixed(0)}KB</span>
                      <button onClick={() => removeFile(i)} className="p-0.5 hover:bg-red-100 rounded">
                        <X className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button
                onClick={startUpload}
                disabled={selectedFiles.length === 0}
                className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </>
        )}

        {(uploading || done) && (
          <>
            {/* Progress bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-700">
                  {done ? 'Upload Complete' : 'Uploading...'}
                </span>
                <span className="text-gray-500">{pct}%</span>
              </div>
              <Progress value={pct} className="h-2" />
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{progress.completed} done, {progress.failed} failed / {progress.total} total</span>
                {!done && progress.eta_seconds > 0 && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatEta(progress.eta_seconds)}
                  </span>
                )}
                {done && doneInfo && (
                  <span>Completed in {doneInfo.total_time}s</span>
                )}
              </div>
            </div>

            {/* File list with statuses */}
            <div className="max-h-56 overflow-y-auto space-y-1 border rounded-lg p-2 mt-3">
              {selectedFiles.map((f, i) => {
                const st = fileStatuses[f.name] || { status: 'queued' };
                return (
                  <div key={i} className="flex items-center gap-2 text-sm py-1.5 px-2 rounded hover:bg-gray-50">
                    {STATUS_ICONS[st.status] || STATUS_ICONS.queued}
                    <span className="flex-1 truncate">{f.name}</span>
                    {st.status === 'processing' && st.stage && (
                      <span className="text-xs text-ugmonk-grey capitalize">{st.stage}</span>
                    )}
                    {st.status === 'failed' && st.error && (
                      <span className="text-xs text-red-500 truncate max-w-[140px]" title={st.error}>
                        {st.error}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {done && (
              <div className="flex justify-end mt-4">
                <Button onClick={handleClose} className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black">
                  Done
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
